import React from 'react';
import ElementTestConfig from '../ElementTestConfig';
import PricingTestConfig from '../PricingTestConfig';

const VariantsConfig = ({ testType, formData, onUpdate, onNext }) => {
    const handlePricingUpdate = (pricingData) => {
        const product = pricingData.selectedProduct;
        
        if (!product) {
            console.error('No product selected');
            return;
        }
        
        const isShopifyProduct = product.variants && Array.isArray(product.variants) && product.variants.length > 0;
        const isCustomProduct = product.type === 'custom' || product.detectionMethod === 'manual';
        
        if (isShopifyProduct) {
            if (!pricingData.variantPrices) {
                console.error('Missing variant prices for Shopify product');
                return;
            }
            
            const optimizedVariantPrices = {};
            product.variants.forEach(variant => {
                const priceData = pricingData.variantPrices[variant.id];
                if (priceData) {
                    optimizedVariantPrices[variant.id] = {
                        variantTitle: variant.title,
                        originalPrice: priceData.original,
                        testPrice: priceData.test,
                        discount: calculateDiscountPercentage(priceData.original, priceData.test)
                    };
                }
            });
            
            onUpdate({
                pricing: {
                    testType: 'pricing',
                    productType: 'shopify',
                    productId: product.id,
                    productTitle: product.title,
                    productImage: product.image,
                    variantPrices: optimizedVariantPrices
                }
            });
        } else if (isCustomProduct) {
            if (!pricingData.testPrice) {
                console.error('Missing test price for custom product');
                return;
            }
            
            onUpdate({
                pricing: {
                    testType: 'pricing',
                    productType: 'custom',
                    productName: product.name,
                    productSelector: product.selector,
                    priceSelector: product.priceSelector,
                    compareAtPriceSelector: product.compareAtPriceSelector || null,
                    pageUrl: product.pageUrl,
                    testPrice: pricingData.testPrice
                }
            });
        } else {
            console.error('Unknown product type:', product);
        }
    };

    const handleElementUpdate = (elementData) => {
        const config = elementData.elementConfig;
        let optimizedElementConfig;

        if (config.elementType === 'button') {
            optimizedElementConfig = {
                testType: 'element',
                elementType: 'button',
                selectedButton: config.selectedButton,
                shouldDuplicate: config.shouldDuplicate,
                modifications: {
                    selector: config.modifications.selector,
                    buttonType: config.modifications.buttonType,
                    buttonName: config.modifications.buttonName,
                    pageUrl: config.modifications.pageUrl,
                    shouldDuplicate: config.modifications.shouldDuplicate,
                    style: {
                        original: config.modifications.style.original,
                        test: config.modifications.style.test
                    },
                    text: {
                        original: config.modifications.text.original,
                        test: config.modifications.text.test
                    },
                    position: {
                        original: config.modifications.position.original,
                        test: config.modifications.position.test
                    }
                }
            };
        } else if (config.elementType === 'image') {
            optimizedElementConfig = {
                testType: 'element',
                elementType: 'image',
                selectedImage: config.selectedImage,
                modifications: {
                    selector: config.modifications.selector,
                    imageName: config.modifications.imageName,
                    pageUrl: config.modifications.pageUrl,
                    imageReplacement: config.modifications.imageReplacement
                }
            };
        }

        onUpdate({
            variants: optimizedElementConfig
        });
    };

    const calculateDiscountPercentage = (original, test) => {
        if (!test || !original || test >= original) return 0;
        return Math.round(((original - test) / original) * 100);
    };

    const handleNext = () => {
        onNext();
    };

    const renderPricingVariants = () => {
        return (
            <PricingTestConfig
                testType={testType}
                formData={formData}
                onUpdate={handlePricingUpdate}
                onNext={handleNext}
                initialProduct={
                    formData.variants ? reconstructPricingProduct(formData.variants) : null
                }
                initialPrices={
                    formData.variants ? reconstructPricingPrices(formData.variants) : null
                }
            />
        );
    };

    const renderElementVariants = () => {
        return (
            <ElementTestConfig
                testType={testType}
                formData={formData}
                onUpdate={handleElementUpdate}
                onNext={handleNext}
                initialConfig={
                    formData.variants ? reconstructElementConfig(formData.variants) : null
                }
            />
        );
    };

    const reconstructPricingProduct = (variants) => {
        if (!variants || variants.testType !== 'pricing') return null;

        return {
            id: variants.productId,
            title: variants.productTitle,
            image: variants.productImage,
            productId: variants.productId.split('/').pop(),
            variants: Object.entries(variants.variantPrices).map(([id, data]) => ({
                id,
                title: data.variantTitle,
                price: data.originalPrice
            }))
        };
    };

    const reconstructPricingPrices = (variants) => {
        if (!variants || variants.testType !== 'pricing') return null;

        const prices = {};
        Object.entries(variants.variantPrices).forEach(([id, data]) => {
            prices[id] = {
                original: data.originalPrice,
                test: data.testPrice
            };
        });
        return prices;
    };

    const reconstructElementConfig = (variants) => {
        if (!variants || variants.testType !== 'element') return null;

        if (variants.elementType === 'button') {
            return {
                elementType: 'button',
                selectedButton: variants.selectedButton,
                shouldDuplicate: variants.shouldDuplicate,
                positionConfig: {
                    original: variants.modifications.position.original,
                    test: variants.modifications.position.test
                }
            };
        } else if (variants.elementType === 'image') {
            return {
                elementType: 'image',
                selectedImage: variants.selectedImage,
                replacementConfig: variants.modifications.imageReplacement
            };
        }

        return {
            elementType: 'button',
            selectedButton: variants.selectedButton,
            modifications: variants.modifications
        };
    };

    const renderVariantsByType = (testType) => {
        switch (testType) {
            case 'pricing':
                return renderPricingVariants();
            case 'element':
                return renderElementVariants();
            default:
                return (
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-200 rounded-2xl p-12 text-center">
                        <div className="max-w-md mx-auto">
                            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="text-3xl">🚧</span>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                Coming Soon
                            </h3>
                            <p className="text-gray-600">
                                Test type "{testType}" is not supported yet
                            </p>
                        </div>
                    </div>
                );
        }
    };

    return (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-6 text-center">
                <h2 className="text-xl font-bold text-gray-900 mb-1">Configure Test Variants</h2>
                <p className="text-sm text-gray-600">Set up your control group and test variation</p>
            </div>

            {/* Variants Content */}
            <div>
                {renderVariantsByType(testType)}
            </div>
        </div>
    );
};

export default VariantsConfig;